import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * 写真 API 操作クラス
 */
export default class Pictures {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET Pictures
   */
  public async gets(userStore: LoginUser, queryParams: { startIndex: number; endIndex: number; order: string }): Promise<Array<PicturesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Pictures, formatParams);
    const response = await this.commonService.executeGet<Array<PicturesGetModel>>(userStore, requestUrl, this.constructor.name, queryParams, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET PicturesCount
   */
  public async getCount(userStore: LoginUser, queryParams: { distributed: boolean }): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Pictures, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(
      userStore,
      `${requestUrl}/count`,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data.count;
  }

  /**
   * PATCH Pictures
   */
  public async patch(userStore: LoginUser, pictureId: number, requestBody: PicturesPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      pictureId: pictureId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Picture, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET PicturePDF
   */
  public async getPicturePDF(userStore: LoginUser, pictureId: number): Promise<string> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      pictureId: pictureId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.PicturePdf, formatParams);
    const response = await this.commonService.executeGet<string>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface PicturesGetModel {
  /** 写真ID */
  id: number;
  /** 施設ID */
  facilityId: number;
  /** 利用者ID */
  residentId: number;
  /** 家族ID */
  familyId: number;
  /** コメント */
  comment?: string;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** 配布日 */
  distributedDatetime?: number;
  /** 画像URL */
  imageUrl: string;
  /** 受信日 */
  receiveDatetime: number;
  /** 利用者名 */
  residentName: string;
  /** 利用者名（ふりがな） */
  residentKanaName: string;
  /** 部屋名 */
  residentRoom: string;
  /** 家族名 */
  familyName: string;
  /** 印刷ボタンを押した人のID */
  distributedId?: string;
  /** 印刷ボタンを押した人の名前 */
  distributedName?: string;
  /** グループID */
  groupId: number;
}

export interface PicturesPatchModel {
  distributedId: string;
  distributedName: string;
  distributedDatetime: number;
}
